import React from 'react';
import { Info, Shield, Calculator, Award, Zap, AlertTriangle, Car, FileText } from 'lucide-react';

/**
 * Компонент с информацией об ОСАГО и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё об ОСАГО: правила, особенности, рекомендации
      </h2>
      
      <div className="prose prose-emerald dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-emerald-500" />
          Что такое ОСАГО?
        </h3>
        <p>
          <strong>ОСАГО</strong> (Обязательное Страхование Автогражданской Ответственности) — это вид страхования, который защищает ответственность владельца транспортного средства перед третьими лицами при ДТП. Если вы стали виновником аварии, страховая компания возместит ущерб пострадавшей стороне в пределах установленных лимитов. ОСАГО обязателен для всех владельцев транспортных средств в России согласно Федеральному закону №40-ФЗ от 25.04.2002.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-emerald-500" />
          Как формируется стоимость ОСАГО?
        </h3>
        <p>
          С 2019 года в России действует индивидуальный подход к расчету стоимости ОСАГО, учитывающий множество факторов. Базовый тариф умножается на ряд коэффициентов, которые могут как увеличивать, так и уменьшать итоговую стоимость полиса.
        </p>
        <ul>
          <li><strong>Базовый тариф:</strong> Устанавливается Центробанком и зависит от типа и мощности транспортного средства.</li>
          <li><strong>Территориальный коэффициент (КТ):</strong> Зависит от региона регистрации автомобиля. В крупных городах с интенсивным движением коэффициент выше.</li>
          <li><strong>Коэффициент возраста и стажа (КВС):</strong> Учитывает возраст и водительский стаж. Молодые и неопытные водители платят больше.</li>
          <li><strong>Коэффициент бонус-малус (КБМ):</strong> Учитывает историю страхования. Безаварийная езда позволяет получить скидку до 50%.</li>
          <li><strong>Коэффициент мощности двигателя (КМ):</strong> Чем мощнее автомобиль, тем выше стоимость полиса.</li>
          <li><strong>Коэффициент сезонности (КС):</strong> Позволяет снизить стоимость полиса при сезонном использовании автомобиля.</li>
          <li><strong>Коэффициент ограничения (КО):</strong> Полис без ограничения количества водителей (мультидрайв) стоит дороже.</li>
        </ul>

        <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800 my-6">
          <h4 className="text-emerald-700 dark:text-emerald-300 font-medium flex items-center gap-2">
            <Award className="w-4 h-4" />
            Как сэкономить на ОСАГО?
          </h4>
          <ul className="text-emerald-600 dark:text-emerald-400 text-sm mb-0">
            <li>Поддерживайте безаварийное вождение для снижения КБМ</li>
            <li>Оформляйте полис с ограниченным списком опытных водителей</li>
            <li>Используйте сезонное страхование, если автомобиль эксплуатируется не круглый год</li>
            <li>Сравнивайте предложения разных страховых компаний</li>
            <li>Избегайте просрочки полиса, чтобы не потерять скидку за безаварийную езду</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-orange-500" />
          Лимиты ответственности по ОСАГО
        </h3>
        <p>
          С 2022 года максимальные выплаты по ОСАГО составляют:
        </p>
        <ul>
          <li><strong>400 000 рублей</strong> — за ущерб имуществу каждого потерпевшего</li>
          <li><strong>500 000 рублей</strong> — за вред здоровью или жизни каждого потерпевшего</li>
          <li>При оформлении ДТП по европротоколу лимит выплат составляет <strong>100 000 рублей</strong> (в Москве, Санкт-Петербурге и их областях — до 400 000 рублей при определенных условиях)</li>
        </ul>
        <p>
          Если ущерб превышает установленные лимиты, разницу придется компенсировать виновнику ДТП самостоятельно или за счет дополнительного страхования (ДСАГО).
        </p>

        <h3 className="flex items-center gap-2">
          <Car className="w-5 h-5 text-indigo-500" />
          Что не покрывает ОСАГО?
        </h3>
        <ul>
          <li>Ущерб, нанесенный автомобилю виновника ДТП</li>
          <li>Моральный вред</li>
          <li>Упущенную выгоду</li>
          <li>Ущерб грузу, перевозимому в автомобиле</li>
          <li>Ущерб при использовании автомобиля в соревнованиях или испытаниях</li>
          <li>Ущерб, причиненный умышленно</li>
          <li>Вред, возникший при использовании автомобиля не по назначению или при управлении лицом, не включенным в полис (при ограниченном списке водителей)</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Что делать при ДТП?
          </h4>
          <ol className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Остановите автомобиль, включите аварийную сигнализацию, выставьте знак аварийной остановки</li>
            <li>Проверьте, есть ли пострадавшие, при необходимости вызовите скорую помощь</li>
            <li>Зафиксируйте все обстоятельства ДТП: сделайте фото- и видеосъемку повреждений, места ДТП, соберите контакты свидетелей</li>
            <li>Вызовите ГИБДД или оформите ДТП по европротоколу (если нет пострадавших и ущерб не превышает установленных лимитов)</li>
            <li>Сообщите о ДТП в свою страховую компанию в течение 5 рабочих дней</li>
          </ol>
        </div>

        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-500" />
          Как оформить ОСАГО?
        </h3>
        <p>
          Оформить ОСАГО можно несколькими способами:
        </p>
        <ul>
          <li><strong>В офисе страховой компании:</strong> Традиционный способ, требует личного присутствия.</li>
          <li><strong>Онлайн на сайте страховщика:</strong> Электронный полис ОСАГО (е-ОСАГО) имеет такую же юридическую силу, как бумажный.</li>
          <li><strong>Через страховых агентов или брокеров:</strong> Удобно при выборе между несколькими страховыми компаниями.</li>
          <li><strong>Через мобильные приложения страховых компаний:</strong> Быстрый способ оформления и продления полиса.</li>
        </ul>
        <p>
          Для оформления ОСАГО необходимы следующие документы:
        </p>
        <ul>
          <li>Паспорт собственника ТС</li>
          <li>Свидетельство о регистрации ТС или ПТС</li>
          <li>Водительские удостоверения всех вписываемых в полис водителей</li>
          <li>Диагностическая карта (для автомобилей старше 4 лет)</li>
        </ul>

        <h3>Европротокол и упрощенное оформление ДТП</h3>
        <p>
          Европротокол позволяет оформить ДТП без вызова ГИБДД при соблюдении следующих условий:
        </p>
        <ul>
          <li>В ДТП участвовало не более двух транспортных средств</li>
          <li>Нет пострадавших</li>
          <li>Оба водителя имеют действующие полисы ОСАГО</li>
          <li>У участников нет разногласий по обстоятельствам ДТП</li>
          <li>Ущерб не превышает 100 000 рублей (в Москве, Санкт-Петербурге и их областях — до 400 000 рублей при использовании технических средств фиксации)</li>
        </ul>
        <p>
          С 2019 года для оформления европротокола можно использовать мобильное приложение «Помощник ОСАГО», которое позволяет заполнить извещение о ДТП в электронном виде.
        </p>

        <h3>Последние изменения в ОСАГО</h3>
        <p>
          Система ОСАГО в России постоянно совершенствуется. Последние важные изменения:
        </p>
        <ul>
          <li>Возможность оформления электронного полиса ОСАГО</li>
          <li>Переход к индивидуальному тарифообразованию</li>
          <li>Приоритет натурального возмещения (ремонта) над денежной выплатой</li>
          <li>Расширение тарифного коридора для более гибкого ценообразования</li>
          <li>Введение электронного европротокола через мобильное приложение</li>
          <li>Повышение лимитов выплат по ОСАГО до 400 000 рублей за имущественный ущерб и 500 000 рублей за вред жизни и здоровью</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор ОСАГО поможет вам рассчитать примерную стоимость полиса с учетом всех актуальных коэффициентов и получить выгодное предложение от надежных страховых компаний!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;